import React, { useState } from "react";
import DSInput from "@/componentsDS/Input";
import Icon from "@/componentsDS/Icon/Icon";
import { IconSize, IconStyle } from "@/componentsDS/Icon/Icon.type";
import parse from "html-react-parser";
import { DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger } from "@dextech-ai/dex-design-system";
import Button from "@/componentsDS/Button/Button";
import { ButtonSize, ButtonType, ButtonVariant } from "@/componentsDS/Button/Button.type";
import Alert from "@/components/Alert/index";
import { AlertType } from "@/components/Alert/alert.type";
import useTermClause from "@/hooks/use-term-clause/index";
import CardConfigure from "@/componentsDS/Modal/CardConfigure";
import { useSelector } from "react-redux";
import _ from "lodash";
import Toast from "@/components/Toast/index";
import PropTypes from 'prop-types';

const ClausePicker = ({ label, name, options, value, onChange }) => (
	<DropdownMenu placement="bottomLeft">
		<DropdownMenuTrigger asChild>
			<Button text={label} type={ButtonType.LINK} variant={ButtonVariant.STANDARD} />
		</DropdownMenuTrigger>
		<DropdownMenuContent>
			{name && <DropdownMenuLabel itemKey="label">{name}</DropdownMenuLabel>}
			<DropdownMenuRadioGroup
				value={String(value)}
				onValueChange={(selected) => onChange(options.filter((item) => String(item.id) === selected))}
			>
				{options.map((item) => (
					<DropdownMenuRadioItem key={item.id} itemKey={String(item.id)} value={String(item.id)}>
						{item.label}
					</DropdownMenuRadioItem>
				))}
			</DropdownMenuRadioGroup>
		</DropdownMenuContent>
	</DropdownMenu>
);

ClausePicker.propTypes = {
	label: PropTypes.node,
	name: PropTypes.string,
	options: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), label: PropTypes.node })).isRequired,
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	onChange: PropTypes.func.isRequired,
};

const ModelTermForm = ({ setCreateModelTerm, termClause, termTemplate, isEdit }) => {
    
    const [updatedValues, setUpdatedValues] = useState(isEdit ? _.merge({}, ...Object.values(termClause.clausePayload)) : {});
    const [errorState, setErrorState] = useState({});
    const [termName, setTermName] = useState('')

    const { createTermClause, updateTermClause, toastList } = useTermClause();
    const profile = useSelector(state => state.auth.profile);

    const validateModelTerm = (sections) => {
        let errorState = {};
        let isError = false;
        sections.map(([key, value]) => {   
            const requiredFields = value.required;
            if(!requiredFields.every(item => {
                return updatedValues.hasOwnProperty(item) || value.properties[item].default
            })) {
                errorState[key] = 'There are incomplete fields. Please complete all fields to proceed.';
                isError = true
            }            
        })
        if(isError) {
            return setErrorState(errorState)
        }
        setErrorState({})
        return true
    }


    const onSubmitHandler = async() => {

        const modelTermSections = Object.entries(termTemplate.elementSchema.properties);
       
        const isValid = validateModelTerm(modelTermSections)
        if(!isValid) {
            return;
        }

        const clausePayload = {};
        
        modelTermSections.map(([key, value]) => {
            const keys = Object.keys(value.properties);
            const keyPayload = {}
            keys.forEach(key => {
                if (updatedValues.hasOwnProperty(key)) {
                    keyPayload[key] = updatedValues[key];
                } else {
                    keyPayload[key] = value.properties[key].default
                }
            });
            clausePayload[key] = keyPayload;
        })

        let payload = { clausePayload }

        let data;
        if (isEdit) {
			data = await updateTermClause(termClause.id, payload);
		} else {
			data = await createTermClause({
				...payload,
				clauseName: termName,
				templateId: termTemplate.templateId,
				orgId: profile?.organizationId,
			});
		}
		if (data) {
			setCreateModelTerm(false);
		}
    }


    const handleClick = (id, val) => {
        setUpdatedValues((prev) => ({ ...prev, [id]: val[0].id }));
    }

	const replacableContent = (domNode, schema) => {
		const properties = schema.properties;
		if (
			domNode.name === "span" &&
			domNode.attribs?.class?.includes("replaceable")
		) {
			const id = domNode.attribs.id;
			const option = properties[id];
			const checkboxOptions = option?.["x-uioptions"]?.options;
			const dropdownOptions = (checkboxOptions?.length > 0 ? checkboxOptions : option?.values || []).map(item => ({
				id: item.value ?? item,
				label: item.label ?? item,
				category: option?.name,
			}));
			const originalText = domNode.children[0]?.data;

			return (
				<div className="replaceable">
					<ClausePicker
						label={updatedValues[id] || option.default || originalText}
						name={option?.name}
						options={dropdownOptions}
						value={updatedValues[id] ?? option.default}
						onChange={(selected) => handleClick(id, selected)}
					/>
				</div>
			);
		}
	};

	const renderSections = () => {
		return Object.entries(termTemplate.elementSchema.properties).map(
			([key, value]) => {
                const innerHtml = termTemplate.templateText[key].replace(/\\n/g, '\n').replace(/\\"/g, '"');
				return (
					<CardConfigure key={key} addHeaderClass={"account-settings__model-term__card-configure"}>
						<CardConfigure.Header>
							<CardConfigure.Header.Title>
								<div>
									<div className="account-settings__create-modal-term__section__name">{value.name}</div>
									<div className="account-settings__create-modal-term__section__info">
										<Icon
											name="information-circle"
											size={IconSize.MEDIUM}
											style={IconStyle.SOLID}
										/>
										<p>{value.helperText}</p>
									</div>
									{errorState[key] && <Alert icon = {<Icon name="information-circle" size={IconSize.MEDIUM} style={IconStyle.SOLID}/>} type={AlertType.ERROR} message={errorState[key]}/>}
								</div>
							</CardConfigure.Header.Title>
						</CardConfigure.Header>
						<CardConfigure.Body>
							<CardConfigure.Body.Col>
								<div>{parse(innerHtml, { replace : (domNode) => replacableContent(domNode, termTemplate.elementSchema.properties[key])})}</div>
							</CardConfigure.Body.Col>
						</CardConfigure.Body>
					</CardConfigure>
				);
			}
		);
	};

	return (
		<>

			<div className="account-settings__create-modal_term">
				<div className="account-settings__create-modal_term__title">
					Fill eBL Template
				</div>
				<hr />
				<div className="account-settings__create-modal_term__subtitle">
					Select and customise the legal terms to be included in your
					electronic bill of lading.
				</div>
				{!isEdit && (
					<DSInput
						name="termName"
						label="Template Name"
						placeholder="Enter Text"
						required={true}
						id="model-term-input"
						fullWidth={true}
						onChange={setTermName}
					/>
				)}
				<div>{renderSections()}</div>
			</div>

			<div className="DS__layout__footer">
				<div className="layout-default__sticky-actions">
					<div className="layout-default__sticky-actions__left">
						<Button
							text="Cancel"
							variant={ButtonVariant.STANDARD}
							type={ButtonType.GHOST}
							onClick={() => {
								setCreateModelTerm(false);
							}}
						/>
					</div>
					<div className="layout-default__sticky-actions__right layout-default--flex">
						<Button
							text="Submit"
							variant={ButtonVariant.STANDARD}
							type={ButtonType.SOLID}
							size={ButtonSize.DEFAULT}
							onClick={onSubmitHandler}
                            disabled={!isEdit && !termName.trim()}
							className="account-settings__modal_term-action"
						/>
					</div>
				</div>
			</div>
			<Toast toastList={toastList} />
		</>
	);
};

export default ModelTermForm;

ModelTermForm.propTypes = {
	setCreateModelTerm: PropTypes.func,
	termClause: PropTypes.object,
	termTemplate: PropTypes.object,
	isEdit: PropTypes.bool,
}